import { useState, useEffect, useRef } from 'react'
import { UUID } from "uuidjs";
import useClient from './useClient'
import cloneDeep from 'lodash.clonedeep'
import merge from 'lodash.merge'
export default function useItem(_itemUUID = null, _selectedSizeUUID = null, _locationUUID = null, _mergeWith = null, _orderTakeAway = false, _order = null) {
  const [assembledItem, setAssembledItem] = useState(null)
  const assembledItemRef = useRef(null)

  const {
    client,
    deviceLocationUUID,
    selectedLocationUUID
  } = useClient()

  const uid = useRef(UUID.generate())
  const currentLocationUUIDRef = useRef(null)
  const selectedSizeUUIDRef = useRef(null)
  const takeAwayRef = useRef(null)
  const mergeWithRef = useRef(null)

  const [locationUUID, setLocationUUID] = useState(null)

  const validateSize = (sizeUUID) => {
    for (let size of assembledItemRef.current?.sizes || []) {
      if (size.uuid === sizeUUID) return true
    }
    return false
  }

  const setSize = (sizeUUID) => {
    if (validateSize(sizeUUID)) {
      if (sizeUUID !== assembledItemRef.current.selectedSizeUUID) {
        selectedSizeUUIDRef.current = sizeUUID
        assembledItemRef.current.selectedSizeUUID = sizeUUID
        assembleItem()
      }
    }
  }

  const setCount = (count) => {
    assembledItemRef.current.count = count
    assembleItem()
  }

  const setQuantity = (quantity) => {
    let { sizeAttributes = null, unitType, package: itemPackage = null } = assembledItemRef.current
    if(sizeAttributes?.scale && unitType === 'kg' && +itemPackage?.weight > 0) {
      if(+itemPackage.weight < +quantity) {
        quantity = +quantity - +itemPackage.weight
      }
    }
    assembledItemRef.current.quantity = +((+quantity || 0).toFixed(3))
    assembleItem()
  }

  const setServingID = (servingID) => {
    assembledItemRef.current.servingID = servingID
    assembleItem()
  }

  const setNotes = (notes) => {
    assembledItemRef.current.notes = notes
    assembleItem()
  }

  const setTakeAway = (takeAway) => {
    assembledItemRef.current.takeAway = takeAway
    assembleItem()
  }

  const setModifierCount = (modifierUUID, count) => {
    for (let modGroup of assembledItemRef.current?.modifierGroups || []) {
      for (let modifier of modGroup?.modifiers || []) {
        if (modifier.uuid === modifierUUID) {
          modifier.count = count
          assembleItem()
        }
      }
    }
  }

  const assembleItem = () => {
    if (assembledItemRef.current == null) return
    let newAssembledItem = client.catalog.assembleItemObject(cloneDeep(assembledItemRef.current), false, currentLocationUUIDRef.current, selectedSizeUUIDRef.current, _order?.order)
    assembledItemRef.current = newAssembledItem
    setAssembledItem(newAssembledItem)
  }

  const onLocationItemUpdated = (data) => {
    if (data.locationUUID === currentLocationUUIDRef.current && data.itemUUID === assembledItemRef.current?.uuid) {
      assembleItem()
    }
  }

  useEffect(() => {
    assembledItemRef.current = assembledItem
  }, [assembledItem])

  useEffect(() => {
    if (locationUUID == null) return
    let changed = false
    if (_itemUUID != null && (assembledItemRef.current?.uuid !== _itemUUID || _mergeWith !== mergeWithRef.current)) {
      assembledItemRef.current = client.catalog.assembleItem(_itemUUID, true, locationUUID)
      if (_mergeWith != null) {
        merge(assembledItemRef.current, _mergeWith)
        mergeWithRef.current = _mergeWith
      }
      changed = true
    }
    if (selectedSizeUUIDRef.current == null || (selectedSizeUUIDRef.current !== _selectedSizeUUID && _selectedSizeUUID != null)) {
      if (_selectedSizeUUID == null) {
        selectedSizeUUIDRef.current = client?.catalog?.items?.[_itemUUID]?.defaultSize
      } else {
        selectedSizeUUIDRef.current = _selectedSizeUUID
      }
      changed = true
    }
    if (takeAwayRef.current !== _orderTakeAway) {
      takeAwayRef.current = _orderTakeAway
      changed = true
    }
    if (changed) assembleItem()
  }, [locationUUID, _itemUUID, _mergeWith, _selectedSizeUUID, _orderTakeAway])

  useEffect(() => {
    if (locationUUID != null && currentLocationUUIDRef.current !== locationUUID) {
      currentLocationUUIDRef.current = locationUUID
      assembleItem()
    }
  }, [locationUUID])

  useEffect(() => {
    let newLocationUUID = deviceLocationUUID != null
      ? deviceLocationUUID // Primary location decision by device token
      : _locationUUID != null
        ? _locationUUID // Free location decision per hook
        : selectedLocationUUID // Secondary location decision from context
    if (newLocationUUID !== currentLocationUUIDRef.current) {
      setLocationUUID(newLocationUUID)
    }
  }, [_locationUUID, deviceLocationUUID, selectedLocationUUID])

  useEffect(() => {
    if (client != null) {
      client.catalog.addCallback(
        'onLocationItemUpdated',
        uid.current,
        onLocationItemUpdated
      )
    }
    return () => {
      // ON UNMOUNT
      if (client != null) {
        client.catalog.removeCallback('onLocationItemUpdated', uid.current)
      }
    }
  }, [client])

  const getUpsales = (isVisible = true) => {
    if (assembledItemRef.current?.uuid == null) return {}
    return client.catalog.getItemUpsales(assembledItemRef.current.uuid, locationUUID, isVisible)
  }

  return {
    assembledItem,
    locationUUID,
    setSize,
    setCount,
    setQuantity,
    setNotes,
    setTakeAway,
    setModifierCount,
    setServingID,
    getUpsales
  }

}
